import {defineSlotRecipe} from '@chakra-ui/react3'

// IMPORTANT: These modules can render CMS content assets and embedded pay-later widgets,
// so these selectors/styles depend on the current rendered HTML structure.
const payLaterWidgetStyles = {
    '& klarna-placement::part(osm-container)': {
        backgroundColor: 'transparent',
        border: 'none',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'spacing-24'
    },
    '& klarna-placement::part(osm-message)': {
        // using negative margin because klarna doesn't expose the text wrapper so its padding can't be changed
        marginLeft: '-0.875rem',
        textStyle: 'body.large'
    },
    // Styles the Klarna CTA link to match Leap's secondary text link appearance:
    // 1. display: 'block' breaks the button onto its own line below the message text.
    // 2. marginTop: 'spacing-24' creates the gap between message text and CTA.
    // 3. borderBottomWidth + borderBottomColor replicate Leap's link underline (textDecoration: 'none' removes Klarna's default browser underline).
    '& klarna-placement::part(osm-cta)': {
        display: 'block',
        width: 'fit-content',
        marginTop: 'spacing-24',
        color: 'text.primary',
        textStyle: 'body.medium',
        textDecoration: 'none',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'border.strong',
        _hover: {
            color: 'text.primary',
            textDecoration: 'none',
            borderBottomColor: 'border.strong'
        }
    }
}

// Shared base styles for usp and uspSummaryStep.
// uspSummaryStep always uses these styles (no responsive breakpoints),
// while usp overrides some properties at the `s` breakpoint.
const uspBaseStyles = {
    moduleContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: '0',
        maxWidth: '100%',
        overflow: 'hidden',
        backgroundColor: 'background.section.default',
        ...payLaterWidgetStyles
    },
    cards: {
        display: 'flex',
        flexDirection: 'row', // Force horizontal to display all cards in one line
        flexWrap: 'nowrap',
        gap: 'spacing-16',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        width: '100%'
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 'spacing-40',
        justifyContent: 'space-between',
        gap: 'spacing-24',
        flex: '1',
        borderRadius: 's',
        backgroundColor: 'background.surface.on-section-inverted',
        height: '100%',
        width: '100%'
    },
    isPayLater: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: 'spacing-24'
    },
    listItem: {
        display: 'flex',
        flex: 'none',
        minWidth: '0',
        width: '100%'
    },
    iconText: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'spacing-24'
    },
    headline: {
        display: 'block',
        color: 'text.secondary',
        textStyle: 'body.medium',
        gap: 'spacing-16'
    },
    copy: {
        paddingTop: '0',
        textAlign: 'left',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        textStyle: 'body.large'
    }
}

const sameCombos = [
    ['default', '3'],
    ['condensed', '3'],
    ['with-overlay', '3'],
    ['default', '5'],
    ['with-overlay', '5'],
    ['default', '6'],
    ['condensed', '6'],
    ['with-overlay', '6'],
    ['usp', '3'],
    ['usp', '5'],
    ['usp', '6']
]

const sameFourCombos = [
    ['default', '4'],
    ['condensed', '4'],
    ['with-overlay', '4'],
    ['usp', '4']
]

export const cardsSlotRecipe = defineSlotRecipe({
    className: 'cards',
    slots: [
        'moduleContainer',
        'cards',
        'card',
        'iconText',
        'headline',
        'cta',
        'copy',
        'overlayIcon',
        'listItem'
    ],

    base: {
        moduleContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: {base: 'spacing-40', s: 'spacing-80'}
        },
        cards: {
            gap: {base: 'spacing-8', s: 'spacing-16'},
            flexWrap: 'wrap',
            flexDirection: {base: 'column', s: 'row'},
            alignItems: 'stretch'
        },
        card: {
            backgroundColor: 'var(--module-surface-bg-color)',
            color: 'text.primary',
            transition: 'transform 0.2s ease',
            borderRadius: 's',
            height: '100%'
        },
        iconText: {
            gap: 'spacing-24',
            flexDirection: 'column',
            alignItems: 'flex-start',
            _icon: {
                width: '6',
                height: '6'
            }
        },
        copy: {
            color: 'text.primary',
            textAlign: 'left'
        },
        overlayIcon: {
            display: 'none'
        }
    },

    variants: {
        variant: {
            default: {
                card: {
                    flexDirection: 'column',
                    padding: 'spacing-40',
                    alignItems: 'start'
                },
                _focusVisible: {padding: 'spacing-40'},

                headline: {display: 'block'},
                copy: {paddingTop: 'spacing-16'}
            },
            condensed: {
                card: {
                    flexDirection: {base: 'row', s: 'column'},
                    padding: {base: 'spacing-20', s: 'spacing-32'},
                    gap: {base: 'spacing-16', s: 'unset'},
                    alignItems: {base: 'center', s: 'start'},
                    justifyContent: {base: 'start', s: 'space-between'}
                },
                _focusVisible: {padding: {base: 'spacing-20', s: 'spacing-32'}},
                headline: {display: 'none'},
                copy: {paddingTop: {base: 0, s: 'spacing-16'}},
                cta: {display: 'none'}
            },
            'with-overlay': {
                card: {
                    position: 'relative',
                    paddingTop: 'spacing-40',
                    paddingX: 'spacing-40',
                    paddingBottom: 'spacing-80',
                    flexDirection: 'column',
                    gap: 'spacing-24',
                    cursor: 'pointer',
                    width: '100%'
                },
                cta: {display: 'none'},
                overlayIcon: {
                    display: 'inline',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: 'spacing-16'
                }
            },
            usp: {
                ...uspBaseStyles,
                moduleContainer: {
                    ...uspBaseStyles.moduleContainer,
                    paddingY: {base: 'spacing-40', s: 'spacing-64'},
                    '--usp-inline-padding': '{spacing.spacing-24}',
                    '--container-inline-padding-start':
                        'calc(var(--usp-inline-padding) + var(--sidebar-inline-padding-start, 0px))',
                    '--container-inline-padding-end':
                        'calc(var(--usp-inline-padding) + var(--sidebar-inline-padding-end, 0px))'
                },
                cards: {
                    ...uspBaseStyles.cards,
                    flexWrap: {base: 'nowrap', s: 'wrap'},
                    gap: {base: '0', s: 'spacing-16'},
                    paddingInline: '0',
                    marginInline: '0',
                    scrollPaddingInline: '0',
                    overflowX: {base: 'auto', s: 'visible'}
                },
                listItem: {
                    ...uspBaseStyles.listItem,
                    flex: {base: 'none', s: '1 1 auto'}
                }
            },
            uspSummaryStep: {
                ...uspBaseStyles,
                moduleContainer: {
                    ...uspBaseStyles.moduleContainer,
                    '--container-inline-padding-start': 'var(--sidebar-inline-padding-start, 0px)',
                    '--container-inline-padding-end': 'var(--sidebar-inline-padding-end, 0px)',
                    backgroundColor: 'background.surface.on-section-inverted',
                    overflow: 'visible'
                },
                card: {
                    ...uspBaseStyles.card,
                    backgroundColor: 'background.surface.on-section-subtle'
                }
            }
        }
    },

    compoundVariants: [
        ...sameCombos.map(([variant, count]) => ({
            variant,
            count,
            css: {
                listItem: {
                    minWidth: {
                        base: '100%',
                        s: 'calc((100% - (var(--leap-spacing-spacing-16) * 2)) / 3)'
                    },
                    flex: '1 1 0'
                }
            }
        })),
        ...sameFourCombos.map(([variant, count]) => ({
            variant,
            count,
            css: {
                listItem: {
                    minWidth: {
                        base: '100%',
                        s: 'calc((100% - (var(--leap-spacing-spacing-16) * 3)) / 4)'
                    }
                },
                cards: {flexWrap: 'nowrap'}
            }
        })),
        {
            variant: 'condensed',
            count: '5',
            css: {
                listItem: {
                    minWidth: {
                        base: '100%',
                        s: 'calc((100% - (var(--leap-spacing-spacing-16) * 4)) / 5)'
                    }
                },
                cards: {flexWrap: 'nowrap'}
            }
        }
    ],

    defaultVariants: {variant: 'default', count: '3'}
})
